// Global fetch auth layer for trusted backend requests.
import { shouldAuthenticate, isRefreshRequest } from './routeBasedApiConfig';

let refreshPromise = null;
let lastRefreshTime = 0;
const REFRESH_COOLDOWN = 1000; // Don't refresh more than once per second
const apiUrl = process.env.REACT_APP_BACKEND_URL || '/';
const refreshEndpoint = `${apiUrl.replace(/\/+$/, '')}/refresh`;

// Store original fetch
const originalFetch = window.fetch;
const getOriginalFetch = () => originalFetch;

// Get current token from localStorage
const getToken = () => localStorage.getItem('token');

// Lightweight JWT expiry check to avoid refreshing on every API call
const isTokenExpired = token => {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (!payload?.exp) return true;
    return payload.exp <= Date.now() / 1000;
  } catch {
    return true;
  }
};

// Centralized logout - clears all auth state and redirects to login
const handleForceLogout = async () => {
  console.warn('Force logout triggered - clearing auth state');
  localStorage.removeItem('token');
  localStorage.removeItem('persist:root3');
  localStorage.removeItem('currentUser');

  try {
    const { store } = await import('../stores/store');
    const { clearAuthData } = await import('../stores/sensor/sensorStore');
    store.dispatch(clearAuthData());
  } catch (e) {
    console.warn('Could not clear Redux auth state:', e);
  }

  window.location.href = '/authentication/sign-in';
};

// Check if a 401 response from the backend is signalling a force logout
// Returns true if the frontend should logout instead of retrying
const shouldForceLogout = async response => {
  // Check header first (fast, no body consumption needed)
  if (response.headers.get('X-Auth-Action') === 'logout') {
    return true;
  }

  // Check body for canRefresh: false
  // We need to clone first because body can only be read once
  try {
    const cloned = response.clone();
    const data = await cloned.json();
    if (data.canRefresh === false || data.errorCode === 'INVALID_TOKEN') {
      return true;
    }
  } catch {
    // Body wasn't JSON - fall through, don't logout based on this alone
  }

  return false;
};

const getRequestHeaders = input => {
  if (typeof Request !== 'undefined' && input instanceof Request) {
    return input.headers;
  }

  return undefined;
};

const cloneFetchInput = input => {
  if (typeof Request !== 'undefined' && input instanceof Request) {
    return input.clone();
  }

  return input;
};

const createFetchInit = (input, init = {}, token) => {
  const headers = new Headers(getRequestHeaders(input));

  if (init.headers) {
    new Headers(init.headers).forEach((value, key) => {
      headers.set(key, value);
    });
  }

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  return {
    ...init,
    headers,
  };
};

// Refresh an expired or missing access token.
const aggressiveRefresh = async () => {
  const now = Date.now();

  // Prevent multiple refreshes in quick succession
  if (refreshPromise || now - lastRefreshTime < REFRESH_COOLDOWN) {
    return refreshPromise || getToken();
  }

  lastRefreshTime = now;

  refreshPromise = (async () => {
    const response = await originalFetch(refreshEndpoint, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache, no-store',
        Pragma: 'no-cache',
      },
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        const forceLogout = await shouldForceLogout(response);
        await handleForceLogout();
        throw new Error(
          forceLogout ? 'Force logout - refresh token invalid' : `Force logout - refresh rejected with status ${response.status}`,
        );
      }
      throw new Error(`Aggressive refresh failed with status ${response.status}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Aggressive refresh failed');
    }

    const accessToken = data.accessToken;
    localStorage.setItem('token', accessToken);

    try {
      const { store } = await import('../stores/store');
      const { setUserToken } = await import('../stores/sensor/sensorStore');
      store.dispatch(setUserToken({ token: accessToken }));
    } catch (e) {
      console.warn('Could not update Redux store:', e);
    }

    return accessToken;
  })();

  try {
    const result = await refreshPromise;
    refreshPromise = null;
    return result;
  } catch (error) {
    refreshPromise = null;

    // Network error - don't logout, the user might just be offline momentarily
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw error;
    }

    // Auth error already handled inside the promise (handleForceLogout called)
    // Re-throw so the caller knows refresh failed
    throw error;
  }
};

// Fallback refresh for 401 responses (backup mechanism)
const fallbackRefresh = async () => {
  const response = await originalFetch(refreshEndpoint, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache, no-store',
      Pragma: 'no-cache',
    },
  });

  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      const forceLogout = await shouldForceLogout(response);
      await handleForceLogout();
      throw new Error(
        forceLogout
          ? 'Force logout - fallback refresh rejected'
          : `Force logout - fallback refresh rejected with status ${response.status}`,
      );
    }
    throw new Error(`Fallback refresh failed with status ${response.status}`);
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Fallback refresh failed');
  }

  const accessToken = data.accessToken;
  localStorage.setItem('token', accessToken);

  try {
    const { store } = await import('../stores/store');
    const { setUserToken } = await import('../stores/sensor/sensorStore');
    store.dispatch(setUserToken({ token: accessToken }));
  } catch (e) {
    console.warn('Could not update Redux store:', e);
  }

  console.log('Fallback refresh successful');
  return accessToken;
};

// Enhanced fetch with on-demand refresh and one 401 retry.
const enhancedFetch = async (url, options = {}) => {
  // Not an API call requiring auth - use original fetch
  if (!shouldAuthenticate(url)) {
    return originalFetch(url, options);
  }

  // Skip refresh for the refresh endpoint itself to avoid infinite loops
  if (isRefreshRequest(url)) {
    return originalFetch(url, options);
  }

  // Refresh only when needed (missing/expired token), not before every API call
  let token = getToken();
  if (!token || isTokenExpired(token)) {
    try {
      token = await aggressiveRefresh();
    } catch (error) {
      // If it was a force logout error, aggressiveRefresh already redirected - just stop
      if (error.message?.includes('Force logout')) {
        throw error;
      }
      // Otherwise (network error etc) fall through with whatever token exists
      console.warn('On-demand refresh failed, proceeding with existing token');
      token = getToken();
    }
  }

  const authenticatedOptions = createFetchInit(url, options, token);

  // Make the API request
  let response = await originalFetch(cloneFetchInput(url), authenticatedOptions);

  // If we get a 401, check if backend wants a force logout before retrying
  if (response.status === 401) {
    const forceLogout = await shouldForceLogout(response);
    if (forceLogout) {
      await handleForceLogout();
      throw new Error('Force logout triggered by API response');
    }

    // Backend didn't signal logout - try one fallback refresh.
    // This also covers persisted sessions where Redux says "signed in" but localStorage lost the access token.
    try {
      const newToken = await fallbackRefresh();
      const retryOptions = createFetchInit(url, options, newToken);
      response = await originalFetch(cloneFetchInput(url), retryOptions);
    } catch (refreshError) {
      console.error('Both on-demand and fallback refresh failed:', refreshError);
      // If fallback called handleForceLogout, we're already redirecting
      throw refreshError;
    }
  }

  return response;
};

// Initialize the global fetch auth patch.
export const initializeGlobalFetchPatch = () => {
  window.fetch = enhancedFetch;
};

// Restore original fetch (useful for cleanup in tests)
export const restoreOriginalFetch = () => {
  window.fetch = originalFetch;
  delete window.debugAuth;
};

// Export the enhanced fetch for direct use if needed
export { enhancedFetch, getOriginalFetch };
